import { useContext, useMemo } from "react";
import type Account from "byzantine/src/Account";
import AccountContext from "../../contexts/AccountContext";

export type NetWorthTotals = {
  totalAssets: number;
  totalLiabilities: number;
  netWorth: number;
  hasDisconnectedExternalAccount: boolean;
};

export function calculateNetWorthTotals(accounts: Account[]): NetWorthTotals {
  const visibleAccounts = accounts.filter((account) => !account.hidden);

  let totalAssets = 0;
  let totalLiabilities = 0;
  visibleAccounts.forEach((account) => {
    const value = account.netWorthValueAsFloat();
    if (account.net_worth_category === "asset") {
      totalAssets += value;
    } else {
      totalLiabilities += value;
    }
  });

  const hasDisconnectedExternalAccount = visibleAccounts.some(
    (account) => account.isExternal() && account.sync_status === "disconnected"
  );

  return {
    totalAssets,
    totalLiabilities,
    netWorth: totalAssets - totalLiabilities,
    hasDisconnectedExternalAccount,
  };
}

/**
 * Narrows accounts to those eligible for net worth: not hidden, and (for
 * external accounts specifically) not still awaiting their first sync -
 * synced_with is only set once a sync provider has actually run at least
 * once, so an account without it has no balance data to contribute yet and
 * would just show up as a confusing $0.00 line. Then further narrows to
 * selectedAccountIds when non-empty. An empty selectedAccountIds means no
 * filter is applied — every eligible account is included. Shared by
 * useNetWorthTotals and the widget's Assets/Liabilities breakdown, so both
 * always agree on which accounts are "currently filtered in".
 */
export function getEligibleAccounts(
  accounts: Account[],
  selectedAccountIds: string[] = []
): Account[] {
  const visibleAccounts = accounts.filter(
    (account) => !account.hidden && (!account.isExternal() || account.synced_with)
  );
  if (selectedAccountIds.length === 0) return visibleAccounts;
  const selected = new Set(selectedAccountIds);
  return visibleAccounts.filter((account) => selected.has(account.id));
}

/**
 * selectedAccountIds narrows which accounts contribute to the totals, on top of
 * the existing hidden-account exclusion. An empty array means no filter is
 * applied (every eligible account is included).
 */
export function useNetWorthTotals(
  selectedAccountIds: string[] = []
): NetWorthTotals {
  const { accounts } = useContext(AccountContext);

  const eligibleAccounts = useMemo(
    () => getEligibleAccounts(accounts, selectedAccountIds),
    [accounts, selectedAccountIds]
  );

  return useMemo(
    () => calculateNetWorthTotals(eligibleAccounts),
    [eligibleAccounts]
  );
}
